// Appearance (legacy user menu "Appearance: Light mode / Dark mode / Auto"): a per-browser preference kept in
// localStorage like legacy `dashboard.parsepos.theme`, applied as `data-mode` on <html>. Unset is Light mode, as legacy
// (`stores/app.ts` loadThemeMode), never the system's scheme.
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useAppliedScheme, type Appearance } from '@radish/ui-web';
import type { BackofficeScheme } from '@radish/ui-tokens';

const KEY = 'radish.backoffice.appearance';

const stored = (): Appearance => {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' || value === 'auto' ? value : 'light';
  } catch {
    return 'light';
  }
};

interface AppearanceApi {
  readonly appearance: Appearance;
  readonly scheme: BackofficeScheme;
  readonly setAppearance: (appearance: Appearance) => void;
}

const Context = createContext<AppearanceApi | null>(null);

export function AppearanceProvider({ children, initial }: { readonly children: ReactNode; readonly initial?: Appearance }) {
  const [appearance, set] = useState<Appearance>(initial ?? stored);
  const scheme = useAppliedScheme(appearance);
  const setAppearance = (next: Appearance) => {
    set(next);
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      // A browser that refuses storage keeps the choice for this page only.
    }
  };
  return <Context.Provider value={{ appearance, scheme, setAppearance }}>{children}</Context.Provider>;
}

export function useAppearance(): AppearanceApi {
  const api = useContext(Context);
  if (api === null) throw new Error('useAppearance needs an <AppearanceProvider>');
  return api;
}
